import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Crown, EyeOff, HelpCircle, Loader2, MoreVertical, ShieldOff, UserMinus, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import ProfileLink from '../../../components/ProfileLink'
import ConfirmModal from '../../../components/ConfirmModal'
import HelpModal from '../../../components/HelpModal'
import { useTeamStore } from '../store/useTeamStore'
import useAppStore from '../../../store/useAppStore'
import {
  assignableRoles, canModerateMember, personName,
  roleLabel, ROLE_RANK, roleRank, teamErrorText
} from '../utils/teamFormat'
import { accentColor, cardPrimaryColor, contentBgColor, roleBadgeStyle, toneBadgeStyle } from '../utils/teamColors'
import { formatAgo } from '../../profile/utils/profileFormat'
import { CARD_CLASS } from '../utils/teamUi'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'

/**
 * Состав банды.
 *
 * `members: null` у чужой банды означает «владелец скрыл состав», а не «пусто».
 * `chat_id` приходит только участникам банды — без него управление составом
 * (роли, кик, передача создателя) невозможно, поэтому кнопки прячутся сами.
 */
const TeamMembersList = ({ team }) => {
  const { t } = useTranslation()
  const { setMemberRole, kickMember, transferOwner, isMutating } = useTeamStore()
  const myChatId = useAppStore((state) => state.user?.telegramid)
  const [openMenu, setOpenMenu] = useState(null)
  const [kickTarget, setKickTarget] = useState(null)
  const [transferTarget, setTransferTarget] = useState(null)
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  const colors = team.colors
  const accent = accentColor(colors)
  const primary = cardPrimaryColor(colors)
  const contentBg = contentBgColor(colors)
  const subtleSurface = `${primary}0D`
  const subtleBorder = `${primary}1F`
  const transferStyle = roleBadgeStyle('owner', colors)
  const kickStyle = toneBadgeStyle(['#FF4B4B', '#B91C1C'], colors)

  const myRole = team.is_member ? team.my_role : null
  const members = team.members

  if (members === null || members === undefined) {
    return (
      <div className={`${CARD_CLASS} flex flex-col items-center p-6 text-center`} style={{ backgroundColor: contentBg, borderColor: subtleBorder }}>
        <span className="mb-2 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-dark-primary">
          <EyeOff size={20} style={{ color: primary }} />
        </span>
        <p className="text-sm" style={{ color: primary }}>
          {t('team.membersHidden', 'Состав скрыт владельцем банды')}
        </p>
      </div>
    )
  }

  const handleRole = async (member, role) => {
    hapticImpact('medium')
    const res = await setMemberRole(member.chat_id, role)
    setOpenMenu(null)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-role-change')
      toast.success(t('team.roleChanged', 'Роль изменена'))
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  const handleKick = async () => {
    if (!kickTarget) return
    const res = await kickMember(kickTarget.chat_id)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-kick')
      setKickTarget(null)
      setOpenMenu(null)
      toast.success(t('team.kicked', 'Игрок исключён'))
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  const handleTransfer = async () => {
    if (!transferTarget) return
    const res = await transferOwner(transferTarget.chat_id)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-transfer')
      setTransferTarget(null)
      setOpenMenu(null)
      toast.success(t('team.transferred', 'Права создателя переданы'))
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  const sorted = [...members].sort((a, b) => {
    const roleDiff = roleRank(b.role) - roleRank(a.role)
    if (roleDiff !== 0) return roleDiff

    const onlineDiff = Number(Boolean(b.online)) - Number(Boolean(a.online))
    if (onlineDiff !== 0) return onlineDiff

    const activeAgoA = Number.isFinite(a.active_ago) ? a.active_ago : Infinity
    const activeAgoB = Number.isFinite(b.active_ago) ? b.active_ago : Infinity
    return activeAgoA === activeAgoB ? 0 : activeAgoA - activeAgoB
  })

  return (
    <div className={`${CARD_CLASS} p-4`} style={{ backgroundColor: contentBg, borderColor: subtleBorder }}>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide" style={{ color: accent }}>
        <span className="flex h-6 w-6 items-center justify-center rounded-lg" style={{ backgroundColor: `${accent}1A` }}>
          <Users size={13} style={{ color: accent }} />
        </span>
        {t('team.members', 'Состав')}
        <span className="tabular-nums">{team.members_count}/{team.max_members}</span>
        <button
          type="button"
          onClick={() => {
            hapticImpact('light')
            setIsHelpOpen(true)
          }}
          className="ml-auto rounded-full p-1.5 transition-colors active:scale-[0.98]"
          style={{ backgroundColor: `${accent}1A`, color: accent }}
          aria-label={t('team.membersHelp.button', 'Справка о составе и ролях')}
        >
          <HelpCircle size={16} />
        </button>
      </h2>

      <div className="space-y-2">
        {sorted.map((member) => {
          const name = personName(member, t)
          // Управление доступно только участникам (chat_id) и только над теми, кто ниже
          const canModerate = member.chat_id != null && canModerateMember(myRole, member.role)
          const roles = canModerate ? assignableRoles(myRole, member.role) : []
          const canTransfer = myRole === 'owner' && member.chat_id != null && member.role !== 'owner'
          const hasActions = roles.length > 0 || canModerate || canTransfer
          const isOpen = openMenu === member.chat_id
          // Это я — тонкая рамка цветом банды; роль и так видна бейджем рядом
          const isMe = member.chat_id != null && String(member.chat_id) === String(myChatId)

          return (
            <div
              key={member.slug || member.chat_id}
              className="rounded-xl border p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              style={{ backgroundColor: subtleSurface, borderColor: isMe ? accent : subtleBorder }}
            >
              <div className="flex items-center gap-3">
                <ProfileLink slug={member.slug} className="shrink-0">
                  <span className="relative block">
                    <span className="block h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-surface">
                      {member.ava ? (
                        <img src={member.ava} alt={name} className="h-full w-full object-cover" />
                      ) : null}
                    </span>
                    {member.online && (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 block h-3 w-3 rounded-full border-2 border-dark-primary"
                        style={{ backgroundColor: accent }}
                      />
                    )}
                  </span>
                </ProfileLink>

                <div className="min-w-0 flex-1">
                  <ProfileLink slug={member.slug} className="block">
                    <span className="block truncate text-sm font-semibold" style={{ color: primary }}>{name}</span>
                  </ProfileLink>
                  <span className="block text-[11px]" style={{ color: primary }}>
                    {member.online
                      ? t('publicProfile.online', 'Онлайн')
                      : formatAgo(member.active_ago, t)}
                  </span>
                </div>

                <span className="shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium" style={roleBadgeStyle(member.role, colors)}>
                  {roleLabel(member.role, t)}
                </span>

                {hasActions && (
                  <button
                    onClick={() => {
                      hapticImpact('light')
                      setOpenMenu(isOpen ? null : member.chat_id)
                    }}
                    className="shrink-0 rounded-lg border p-1.5 transition-all active:scale-[0.98]"
                    style={{ borderColor: subtleBorder, color: primary }}
                    aria-label={t('team.manageMember', 'Управление участником')}
                  >
                    <MoreVertical size={14} />
                  </button>
                )}
              </div>

              {/* Панель управления участником */}
              {isOpen && hasActions && (
                <div className="mt-3 flex flex-wrap gap-2 border-t pt-3" style={{ borderColor: subtleBorder }}>
                  {roles.map((role) => (
                    <button
                      key={role}
                      onClick={() => handleRole(member, role)}
                      disabled={isMutating}
                      className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all active:scale-[0.98] disabled:opacity-50"
                      style={{ backgroundColor: subtleSurface, borderColor: subtleBorder, color: primary }}
                    >
                      {isMutating
                        ? <Loader2 size={12} className="animate-spin" />
                        : (roleRank(role) < ROLE_RANK[member.role] ? <ShieldOff size={12} /> : <Crown size={12} />)}
                      {roleLabel(role, t)}
                    </button>
                  ))}

                  {canTransfer && (
                    <button
                      onClick={() => setTransferTarget(member)}
                      disabled={isMutating}
                      className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all active:scale-[0.98] disabled:opacity-50"
                      style={transferStyle}
                    >
                      <Crown size={12} />
                      {t('team.transferOwner', 'Сделать создателем')}
                    </button>
                  )}

                  {canModerate && (
                    <button
                      onClick={() => setKickTarget(member)}
                      disabled={isMutating}
                      className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all active:scale-[0.98] disabled:opacity-50"
                      style={kickStyle}
                    >
                      <UserMinus size={12} />
                      {t('team.kick', 'Исключить')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('team.membersHelp.title', 'Состав и роли')}
        steps={[
          {
            title: t('team.membersHelp.slotsTitle', 'Дополнительные места'),
            description: t('team.membersHelp.slotsDescription', 'С ростом уровня банды открываются новые слоты, и в неё можно принимать больше участников.')
          },
          {
            title: t('team.membersHelp.ownerTitle', 'Создатель'),
            description: t('team.membersHelp.ownerDescription', 'Главный участник банды. Назначает заместителей и может управлять казной, заявками и составом.')
          },
          {
            title: t('team.membersHelp.deputyTitle', 'Заместитель'),
            description: t('team.membersHelp.deputyDescription', 'Может управлять казной, принимать и отклонять заявки, а также исключать участников.')
          },
          {
            title: t('team.membersHelp.moderatorTitle', 'Модератор'),
            description: t('team.membersHelp.moderatorDescription', 'Может принимать и отклонять заявки и исключать участников, но не управляет казной.')
          },
          {
            title: t('team.membersHelp.memberTitle', 'Участник'),
            description: t('team.membersHelp.memberDescription', 'Не имеет прав управления бандой.')
          }
        ]}
        highlightLastStep={false}
      />

      <ConfirmModal
        isOpen={kickTarget != null}
        onClose={() => setKickTarget(null)}
        onConfirm={handleKick}
        isLoading={isMutating}
        title={t('team.kickTitle', 'Исключить из банды?')}
        message={t('team.kickMessage', '{{name}} потеряет доступ к банде. Заявку он сможет подать заново.', {
          name: kickTarget ? personName(kickTarget, t) : ''
        })}
        confirmText={t('team.kick', 'Исключить')}
      />

      <ConfirmModal
        isOpen={transferTarget != null}
        onClose={() => setTransferTarget(null)}
        onConfirm={handleTransfer}
        isLoading={isMutating}
        title={t('team.transferTitle', 'Передать права создателя?')}
        message={t('team.transferMessage', '{{name}} станет создателем банды без подтверждения, а ты — заместителем. Отменить это сможет только новый создатель.', {
          name: transferTarget ? personName(transferTarget, t) : ''
        })}
        confirmText={t('team.transferConfirm', 'Передать')}
      />
    </div>
  )
}

export default TeamMembersList
